import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import type { WizardStep } from '../steps';
import type { CreateSyncWizardState } from '../use-create-sync-wizard';
import { DatabaseStep } from './database-step';
import { DetailsStep } from './details-step';
import { QueryStep } from './query-step';
import { RelationshipsStep } from './relationships-step';
import { ReviewStep } from './review-step';
import { SettingsStep } from './settings-step';

type WizardStepContentProps = {
  step: WizardStep;
  workspaceId: string;
  type: SqlDatabaseType;
  wizard: CreateSyncWizardState;
};

/** The body of the current step, reading everything from the wizard's state. */
export function WizardStepContent({ step, workspaceId, type, wizard }: WizardStepContentProps) {
  const isReadOnly = !wizard.canCreate;
  switch (step) {
    case 'details':
      return (
        <DetailsStep
          draft={wizard.draft}
          isReadOnly={isReadOnly}
          onChange={wizard.setDraftFields}
          onSubmit={wizard.onDetailsSubmit}
        />
      );
    case 'settings':
      return (
        <SettingsStep
          workspaceId={Number(workspaceId)}
          draft={wizard.draft}
          objectTypes={wizard.objectTypes}
          canIdentifyByEmail={wizard.canIdentifyByEmail}
          canCreateSegment={wizard.canCreateSegment}
          isReadOnly={isReadOnly}
          onChange={wizard.setDraftFields}
          onSubmit={wizard.onSettingsSubmit}
        />
      );
    case 'database':
      return (
        <DatabaseStep
          type={type}
          databases={wizard.databases}
          isReadOnly={isReadOnly}
          sqlServerIp={wizard.sqlServerIp}
          state={wizard.databaseStep}
        />
      );
    case 'query':
    case 'relationships':
      return <QueryStepContent step={step} type={type} wizard={wizard} />;
    case 'review':
      return (
        <ReviewStep
          workspaceId={workspaceId}
          draft={wizard.draft}
          database={wizard.database}
          syncTarget={wizard.syncTarget}
          segment={wizard.segment}
        />
      );
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

type QueryStepContentProps = {
  step: 'query' | 'relationships';
  type: SqlDatabaseType;
  wizard: CreateSyncWizardState;
};

/** The two query-editing steps, which share the preview machinery from `useQuerySteps`. */
function QueryStepContent({ step, type, wizard }: QueryStepContentProps) {
  const { draft, querySteps } = wizard;
  const isReadOnly = !wizard.canCreate;
  if (step === 'query') {
    return (
      <QueryStep
        type={type}
        draft={draft}
        objectTypeName={wizard.objectTypes.find((objectType) => objectType.id === draft.object_type_id)?.name}
        isNewImport={wizard.isNewImport}
        isReadOnly={isReadOnly}
        canIdentifyByEmail={wizard.canIdentifyByEmail}
        blockedReason={querySteps.runBlockedReason}
        run={querySteps.queryRun}
        onQueryChange={querySteps.setQuery}
        onLocationChange={querySteps.setLocation}
        onRun={querySteps.runQuery}
        onGoToSettings={() => wizard.selectStep('settings')}
        onTimeoutDismiss={() => querySteps.dismissTimeout(false)}
        onTimeoutContinue={() => querySteps.continueAfterTimeout(false)}
      />
    );
  }
  return (
    <RelationshipsStep
      type={type}
      draft={draft}
      relationship={querySteps.relationship}
      objectTypes={wizard.objectTypes}
      isReadOnly={isReadOnly}
      canIdentifyByEmail={wizard.canIdentifyByEmail}
      blockedReason={querySteps.runBlockedReason}
      run={querySteps.relationshipsRun}
      onChange={querySteps.setRelationship}
      onRun={querySteps.runRelationshipsQuery}
      onTimeoutDismiss={() => querySteps.dismissTimeout(true)}
      onTimeoutContinue={() => querySteps.continueAfterTimeout(true)}
    />
  );
}
